import { Component, inject } from '@angular/core';

import { ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { finalize } from 'rxjs';

import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

// Composants
import { FormBaseComponent } from 'src/app/@shared/components/abstract/form-base.component';
import { MessageComponent } from 'src/app/@shared/components/message/message.component';
import {
    LegalConsentDialogComponent,
    LegalConsentDialogData
} from 'src/app/legal/components/legal-consent-dialog/legal-consent-dialog.component';
import { LegalDocumentTypeEnum } from 'src/app/@shared/enums/legal-document-type.enum';

// Services
import { AuthenticationService } from 'src/app/@core/authentication/services/authentication.service';

// Utils
import { Logger } from 'src/app/@core/services/logger/logger.service';
import { ODataMessage } from 'src/app/@shared/utilities/odata/message';

const LOGGER = new Logger('SubscribeFormComponent');

@Component({
    selector: 'app-subscribe-form',
    imports: [
        ReactiveFormsModule,
        RouterModule,
        ButtonModule,
        InputTextModule,
        PasswordModule,
        ToggleSwitchModule,
        MessageComponent,
    ],
    templateUrl: './subscribe-form.component.html',
    styleUrls: ['./subscribe-form.component.scss']
})
export class SubscribeFormComponent extends FormBaseComponent {
    private _authenticationService = inject(AuthenticationService);

    public showSuccessMessage: boolean = false;
    // Plafond de débit atteint (HTTP 429, #1059) : bannière explicative, sans toast
    public throttledMessage: ODataMessage | null = null;

    protected override buildForm() {
        this.form = this.formBuilder.group({
            firstName: ['', Validators.required],
            lastName: ['', Validators.required],
            email: ['', Validators.required],
            password: ['', Validators.required],
            confirmPassword: ['', Validators.required],
            cguAccepted: [false, Validators.requiredTrue],
            privacyAccepted: [false, Validators.requiredTrue],
        });
    }

    public openCguDialog(event: Event): void {
        event.preventDefault();
        const data: LegalConsentDialogData = { documentType: LegalDocumentTypeEnum.CGU, mode: 'info' };
        this.openDialog(LegalConsentDialogComponent, 'LEGAL_CONSENT_DIALOG', data);
    }

    public openPrivacyDialog(event: Event): void {
        event.preventDefault();
        const data: LegalConsentDialogData = { documentType: LegalDocumentTypeEnum.PRIVACY, mode: 'info' };
        this.openDialog(LegalConsentDialogComponent, 'LEGAL_CONSENT_DIALOG', data);
    }

    private _displaySuccess(): void {
        this.showSuccessMessage = true;
    }

    public subscribe(): void {
        this.loading = true;
        this.throttledMessage = null;
        const register$ = this._authenticationService.register$(this.form.value);
        register$
            .pipe(
                finalize(() => {
                    this.form.markAsPristine();
                    this.loading = false;
                })
            )
            .subscribe({
                next: () => {
                    this._displaySuccess();
                },
                error: (error: any) => {
                    this.throttledMessage = error?.status === 429 ? (error.messages?.[0] ?? null) : null;
                    Object.keys(error?.details).forEach((key) => {
                        let control = this.form.get(key);
                        control?.setErrors({ invalid: true, message: error.details[key].longText });
                    })
                },
            });
    }

}
